import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  beginLogin,
  getStoredToken,
  handleRedirectCallback,
  isOAuthConfigured,
  refreshAccessToken,
} from '@/auth/oauth.js';
import { resolveTenantUrlParam } from '@/lib/tenant-url.js';

import {
  CONNECTION_EVENTS,
  type DesignTokens,
  type ExtensionDetails,
  type HandshakeAckPayload,
  describeHandshakeAckFailure,
  parseHandshakeAck,
  parseTokenRefresh,
} from './protocol.js';
import { buildMockAck, isRunningStandalone } from './mock-host.js';

export type HandshakeStatus = 'connecting' | 'connected' | 'error';

export interface HandshakeState {
  status: HandshakeStatus;
  error: string | null;
  /** Bearer JWT for the admin-api. */
  jwt: string | null;
  /** ISO-ish expires-at as delivered by the host (Unix seconds, stringified). */
  expires: string | null;
  /** Tenant + extension context delivered by the host. */
  extensionDetails: ExtensionDetails | null;
  /** Live design tokens; consumers apply these to :root via `useTenantTheme`. */
  designTokens: DesignTokens | null;
  /** Origin of the Kadanza host (allowed `postMessage` target). */
  hostOrigin: string | null;
  /** Locale from the host (e.g. `en-US`). */
  locale: string | null;
  /** Ask the host for a fresh JWT (no-op standalone). */
  requestTokenRefresh: () => void;
}

function logHandshake(message: string): void {
  console.log(`[handshake] ${message}`);
}

function validateTenantUrl(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url.origin;
  } catch {
    return null;
  }
}

/**
 * Implements the Kadanza Extension Page handshake.
 *
 * 1. Read `tenantUrl` query param → derive `ALLOWED_ORIGIN`.
 * 2. Post `HANDSHAKE_INIT` to `window.parent`.
 * 3. Listen for `HANDSHAKE_ACK` and `TOKEN_REFRESH` on the same origin.
 * 4. In dev (no parent iframe), synthesise an ACK from `VITE_DEV_*` env vars
 *    so the app boots standalone at `http://localhost:4199`.
 */
export function useHandshake(): HandshakeState {
  const [payload, setPayload] = useState<HandshakeAckPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tenantUrl = useMemo(() => resolveTenantUrlParam(), []);

  const allowedOrigin = useMemo(() => validateTenantUrl(tenantUrl), [tenantUrl]);
  const allowedOriginRef = useRef<string | null>(allowedOrigin);
  allowedOriginRef.current = allowedOrigin;
  const payloadRef = useRef<HandshakeAckPayload | null>(null);
  payloadRef.current = payload;

  const postInit = useCallback(() => {
    const origin = allowedOriginRef.current;
    if (!origin || window.parent === window) return;
    try {
      window.parent.postMessage({ type: CONNECTION_EVENTS.handshakeInit }, origin);
    } catch (e) {
      console.warn('[handshake] failed to send HANDSHAKE_INIT', e);
    }
  }, []);

  const requestTokenRefresh = useCallback(() => {
    if (window.parent === window) {
      if (isOAuthConfigured()) {
        void (async () => {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            setPayload((prev) =>
              prev
                ? { ...prev, authToken: { jwt: refreshed.jwt, expires: refreshed.expires } }
                : prev,
            );
            logHandshake('token refreshed (standalone OAuth)');
          } else {
            await beginLogin();
          }
        })();
      }
      return;
    }
    const origin = allowedOriginRef.current;
    if (!origin) return;
    try {
      window.parent.postMessage({ type: CONNECTION_EVENTS.requestTokenRefresh }, origin);
    } catch (e) {
      console.warn('[handshake] failed to request token refresh', e);
    }
  }, []);

  useEffect(() => {
    if (isRunningStandalone()) {
      if (isOAuthConfigured()) {
        let cancelled = false;
        logHandshake('standalone mode — signing in via OAuth…');
        void (async () => {
          try {
            const fromRedirect = await handleRedirectCallback();
            const token = fromRedirect ?? getStoredToken();
            if (!token) {
              await beginLogin();
              return;
            }
            if (!cancelled) {
              setPayload(buildMockAck({ jwt: token.jwt, expires: token.expires }));
              logHandshake('connected (standalone OAuth)');
            }
          } catch (e) {
            if (!cancelled) {
              const msg = e instanceof Error ? e.message : 'OAuth sign-in failed';
              console.warn(`[handshake] failed — ${msg}`);
              setError(msg);
            }
          }
        })();
        return () => {
          cancelled = true;
        };
      }

      setPayload(buildMockAck());
      logHandshake('connected (standalone dev mock)');
      return;
    }

    if (!allowedOrigin) {
      const msg = 'Missing or invalid `tenantUrl` query parameter.';
      console.warn(`[handshake] failed — ${msg}`);
      setError(msg);
      return;
    }

    logHandshake(`waiting for host (${allowedOrigin})…`);

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== allowedOrigin) return;

      const msgType =
        event.data && typeof event.data === 'object'
          ? (event.data as { type?: unknown }).type
          : undefined;

      if (msgType === CONNECTION_EVENTS.handshakeAck) {
        const ack = parseHandshakeAck(event.data);
        if (ack) {
          const { tenantDomain, tenantId } = ack.payload.extensionDetails;
          logHandshake(`connected (tenant ${tenantDomain || tenantId})`);
          setPayload(ack.payload);
          setError(null);
          return;
        }

        const reasons = describeHandshakeAckFailure(event.data);
        console.warn(
          `[handshake] failed — invalid HANDSHAKE_ACK${reasons.length ? `: ${reasons.join('; ')}` : ''}`,
        );
        return;
      }

      if (msgType === CONNECTION_EVENTS.tokenRefresh) {
        const refresh = parseTokenRefresh(event.data);
        if (refresh) {
          logHandshake('token refreshed');
          setPayload((prev) =>
            prev ? { ...prev, authToken: refresh.payload.authToken } : prev,
          );
          return;
        }

        console.warn('[handshake] failed — invalid TOKEN_REFRESH');
      }
    };

    window.addEventListener('message', handleMessage);
    postInit();

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !payloadRef.current) postInit();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [allowedOrigin, postInit]);

  const status: HandshakeStatus = payload ? 'connected' : error ? 'error' : 'connecting';

  return {
    status,
    error,
    jwt: payload?.authToken.jwt ?? null,
    expires: payload?.authToken.expires ?? null,
    extensionDetails: payload?.extensionDetails ?? null,
    designTokens: payload?.designTokens ?? null,
    hostOrigin: allowedOrigin,
    locale: payload?.extensionDetails.locale ?? null,
    requestTokenRefresh,
  };
}
